import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Spin,
  Alert,
  Typography,
  Form,
  Input,
  Tabs,
  message
} from 'antd';
import {
  WechatOutlined,
  ReloadOutlined,
  ScanOutlined,
  UserOutlined,
  LockOutlined,
  LinkOutlined
} from '@ant-design/icons';
import wechatService, { WeChatQrCodeResponse } from '../services/wechat';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface WeChatLoginProps {
  visible?: boolean;
  onClose?: () => void;
  onSuccess: (userInfo: any, token: string) => void;
  mode?: 'login' | 'bind'; // login: 直接登录, bind: 绑定到现有账户
  bindCurrentUser?: boolean; // 绑定当前已登录用户时使用
  title?: string;
  embed?: boolean;
  type?: 'open' | 'mp'; // open: 开放平台(默认), mp: 公众号
}

const WeChatLogin: React.FC<WeChatLoginProps> = ({
  visible = false,
  onClose,
  onSuccess,
  mode = 'login',
  bindCurrentUser = false,
  title,
  embed = false,
  type = 'open'
}) => {
  const [loading, setLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<WeChatQrCodeResponse['data'] | null>(null);
  const [qrCodeStatus, setQrCodeStatus] = useState<'pending' | 'success' | 'expired' | 'error'>('pending');
  const [statusMessage, setStatusMessage] = useState('');
  const [activeTab, setActiveTab] = useState(mode === 'bind' && !bindCurrentUser ? 'bind' : 'qrcode');
  const [bindForm] = Form.useForm();
  const pollingRef = useRef<boolean>(false);
  const wxLoginRef = useRef<HTMLDivElement | null>(null);
  const isActive = embed ? visible !== false : visible;

  // 动态加载微信官方登录脚本
  const ensureWxLoginScript = async () => {
    if ((window as any).WxLogin) return true;
    return new Promise<boolean>((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // 生成二维码
  const generateQrCode = async () => {
    setLoading(true);
    setQrCodeStatus('pending');
    setStatusMessage('');

    try {
      let result;
      if (type === 'mp') {
        result = await wechatService.generateMpQrCode({ mode: bindCurrentUser ? 'bind' : 'login' });
      } else {
        result = await wechatService.generateQrCode({ mode: bindCurrentUser ? 'bind' : 'login' });
      }

      if (result.success && result.data) {
        setQrCodeData(result.data);
        startPolling(result.data.state);
      } else {
        setQrCodeStatus('error');
        setStatusMessage(result.message || '生成二维码失败');
      }
    } catch (error) {
      setQrCodeStatus('error');
      setStatusMessage('网络错误，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 开始轮询检查扫码状态
  const startPolling = (state: string) => {
    pollingRef.current = true;

    // 自定义轮询函数
    const poll = async () => {
      let attempts = 0;
      const maxAttempts = 120;

      const check = async () => {
        if (!pollingRef.current || attempts >= maxAttempts) return;
        attempts++;

        try {
          let statusResponse;
          if (type === 'mp') {
            statusResponse = await wechatService.checkMpQrCodeStatus(state);
          } else {
            statusResponse = await wechatService.checkQrCodeStatus(state);
          }

          if (!pollingRef.current) return;

          if (statusResponse.success && statusResponse.data) {
            const { status, user, access_token } = statusResponse.data;

            if (status === 'success') {
              setQrCodeStatus('success');
              setStatusMessage('扫码成功，正在登录...');

              if (mode === 'bind' && bindCurrentUser) {
                // MP 模式下，检查状态接口可能直接返回了绑定结果
                // 但为了保持一致性，我们这里还是调用专门的绑定/检查逻辑
                // 不过对于 MP，handleOfficialAccountCallback 已经处理了绑定，
                // checkMpQrCodeStatus 返回 success 时意味着已经处理完毕。
                // 所以如果是 MP 且 bindCurrentUser，且 user 存在，说明绑定成功。

                if (type === 'mp') {
                  message.success('微信公众号绑定成功');
                  onSuccess(user, access_token || '');
                  handleClose();
                  return;
                }

                handleBindCurrentUser(state);
                return;
              }

              if (user && access_token) {
                setTimeout(() => {
                  onSuccess(user, access_token);
                  handleClose();
                }, 1000);
              } else {
                // 只有开放平台可能需要这一步，公众号通常直接返回 user
                handleWeChatLogin(state);
              }
              return; // 停止轮询
            } else if (status === 'expired') {
              setQrCodeStatus('expired');
              setStatusMessage('二维码已过期，请点击刷新重新获取');
              return; // 停止轮询
            }
          } else {
            // 忽略临时网络错误，继续轮询? 或者报错
            // 这里选择继续
          }
        } catch (e) {
          console.error('Poll error', e);
        }

        if (pollingRef.current) {
          setTimeout(check, 1000);
        }
      }
      check();
    };

    poll();
  };

  // 将微信返回的链接参数解析并使用官方 wxLogin 渲染二维码
  useEffect(() => {
    const renderWxLogin = async () => {
      // 如果是公众号登录 (mp)，直接显示图片，不需要加载 WxLogin 脚本
      if (type === 'mp') return;

      if (!qrCodeData?.qr_code_url || !wxLoginRef.current) return;
      const ok = await ensureWxLoginScript();
      if (!ok || !(window as any).WxLogin) {
        setStatusMessage('加载微信登录组件失败');
        setQrCodeStatus('error');
        return;
      }

      try {
        const url = new URL(qrCodeData.qr_code_url);
        const params = url.searchParams;
        const appid = params.get('appid') || '';
        const scope = params.get('scope') || 'snsapi_login';
        const state = params.get('state') || qrCodeData.state;
        const redirectUri = params.get('redirect_uri') || '';

        wxLoginRef.current.innerHTML = '';

        // @ts-ignore 微信脚本挂在 window 上
        // eslint-disable-next-line new-cap
        new (window as any).WxLogin({
          self_redirect: false,
          id: wxLoginRef.current.id,
          appid,
          scope,
          state,
          redirect_uri: decodeURIComponent(redirectUri),
          style: '',
          href: '',
        });
      } catch (e) {
        setStatusMessage('渲染微信二维码失败');
        setQrCodeStatus('error');
      }
    };

    renderWxLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCodeData?.qr_code_url]);

  // 处理微信登录
  const handleWeChatLogin = async (state: string) => {
    try {
      const result = await wechatService.loginWithWeChat(state);

      if (result.success && result.data) {
        message.success('微信登录成功');
        onSuccess(result.data.user, result.data.access_token);
        handleClose();
      } else {
        setQrCodeStatus('error');
        setStatusMessage(result.message || '登录失败');
      }
    } catch (error) {
      setQrCodeStatus('error');
      setStatusMessage('登录失败，请重试');
    }
  };

  // 处理绑定微信账户
  const handleBindWeChat = async (values: { email: string; password: string }) => {
    if (!qrCodeData?.state) {
      message.error('请先扫描二维码');
      return;
    }

    setLoading(true);
    try {
      const result = await wechatService.bindWeChatToAccount({
        email: values.email,
        password: values.password,
        state: qrCodeData.state
      });

      if (result.success && result.data) {
        message.success('微信绑定成功');
        onSuccess(result.data.user, result.data.access_token);
        handleClose();
      } else {
        message.error(result.message || '绑定失败');
      }
    } catch (error) {
      message.error('绑定失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 绑定当前登录用户
  const handleBindCurrentUser = async (state: string) => {
    setLoading(true);
    try {
      const result = await wechatService.bindWeChatToCurrentUser(state);
      if (result.success && result.data) {
        message.success('微信绑定成功');
        onSuccess(result.data.user, '');
        handleClose();
      } else {
        setQrCodeStatus('error');
        setStatusMessage(result.message || '绑定失败');
      }
    } catch (error) {
      setQrCodeStatus('error');
      setStatusMessage('绑定失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    pollingRef.current = false;
    setQrCodeData(null);
    setQrCodeStatus('pending');
    setStatusMessage('');
    bindForm.resetFields();
    onClose?.();
  };

  // 刷新二维码
  const handleRefresh = () => {
    pollingRef.current = false;
    generateQrCode();
  };

  // 组件挂载时生成二维码
  useEffect(() => {
    if (isActive && activeTab === 'qrcode') {
      generateQrCode();
    }

    // 组件卸载时停止轮询
    return () => {
      pollingRef.current = false;
    };
  }, [isActive, activeTab]);

  useEffect(() => {
    if (!isActive) {
      pollingRef.current = false;
      setQrCodeData(null);
      setQrCodeStatus('pending');
      setStatusMessage('');
    }
  }, [isActive]);

  // 渲染二维码区域
  const renderQrCode = (showStatus: boolean = true) => {
    if (loading) {
      return showStatus ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>正在生成二维码...</div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
          <Spin size="small" />
        </div>
      );
    }

    const container = (
      <div
        id="wechat-login-container"
        ref={wxLoginRef}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        {type === 'mp' && qrCodeData?.qr_code_url ? (
          // 公众号二维码直接显示图片
          <img
            src={qrCodeData.qr_code_url}
            alt="微信公众号二维码"
            style={{ width: '200px', height: '200px' }}
          />
        ) : (
          !qrCodeData?.qr_code_url && (
            <div style={{
              width: '200px',
              height: '200px',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ScanOutlined style={{ fontSize: '48px', color: '#ccc' }} />
            </div>
          )
        )}
      </div>
    );

    if (!showStatus) {
      return container;
    }

    if (!qrCodeData) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Alert message="二维码生成失败" type="error" showIcon />
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} style={{ marginTop: '16px' }}>
            重新生成
          </Button>
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        {container}

        <div style={{ marginBottom: '12px', marginTop: '12px' }}>
          {qrCodeStatus === 'pending' && (
            <Text type="secondary">
              <ScanOutlined /> 请使用微信扫描二维码
            </Text>
          )}
          {qrCodeStatus === 'success' && (
            <Text type="success">
              ✓ 扫码成功，正在登录...
            </Text>
          )}
          {qrCodeStatus === 'expired' && (
            <Text type="warning">
              ⚠ 二维码已过期
            </Text>
          )}
          {qrCodeStatus === 'error' && (
            <Text type="danger">
              ✗ {statusMessage}
            </Text>
          )}
        </div>

        {(qrCodeStatus === 'expired' || qrCodeStatus === 'error') && (
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新二维码
          </Button>
        )}
      </div>
    );
  };

  // 渲染绑定表单
  const renderBindForm = () => {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={5}>绑定微信到现有账户</Title>
          <Text type="secondary">请输入您的账户信息，然后扫描二维码完成绑定</Text>
        </div>

        <Form
          form={bindForm}
          layout="vertical"
          onFinish={handleBindWeChat}
        >
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="请输入您的邮箱" />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入您的密码" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block icon={<LinkOutlined />}>
              绑定微信
            </Button>
          </Form.Item>
        </Form>

        <div style={{ marginTop: '20px' }}>
          <Alert
            message="绑定说明"
            description="绑定成功后，您可以使用微信扫码直接登录，无需输入邮箱密码。"
            type="info"
            showIcon
          />
        </div>
      </div>
    );
  };

  const content = mode === 'bind' && !bindCurrentUser ? (
    <Tabs activeKey={activeTab} onChange={setActiveTab}>
      <TabPane tab="账户绑定" key="bind" icon={<UserOutlined />}>
        {renderBindForm()}
      </TabPane>
      <TabPane tab="扫码确认" key="qrcode" icon={<WechatOutlined />}>
        {renderQrCode()}
      </TabPane>
    </Tabs>
  ) : (
    renderQrCode()
  );

  if (embed) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {renderQrCode(false)}
      </div>
    );
  }

  return (
    <Modal
      title={title || (mode === 'bind' ? '' : '')}
      open={isActive}
      onCancel={handleClose}
      footer={null}
      width={400}
      centered
    >
      {content}
    </Modal>
  );
};

export default WeChatLogin;
